/**
 * replaceCodeBlockContent
 *
 * Replaces the code inside a specific fenced code block in a markdown content
 * string, identified by language tag and occurrence index.
 *
 * This is used by the CodeBlock save feature to reconstruct the full message
 * content string with the user's edited code before POSTing it to Firestore.
 *
 * @param {string} content    - Full markdown message content
 * @param {string} langTag    - Language tag as it appears after the backticks (e.g. "html-viz")
 * @param {number} blockIndex - 0-based index among all fenced blocks with this langTag
 * @param {string} newCode    - Replacement code (no surrounding backticks)
 * @returns {string}          - Updated content string, or original if block not found
 */
export function replaceCodeBlockContent(content, langTag, blockIndex, newCode) {
  // Matches ``` followed by the exact lang tag, then any content, then closing ```
  // Uses non-greedy match so consecutive blocks are each captured individually
  const pattern = new RegExp(
    "(`{3}" + escapeRegex(langTag) + "\\n)([\\s\\S]*?)(`{3})",
    "g"
  );

  let matchCount = 0;
  let result = content.replace(pattern, (match, open, _body, close) => {
    if (matchCount === blockIndex) {
      matchCount++;
      // Ensure the replacement code ends with a newline before the closing fence
      const normalised = newCode.endsWith("\n") ? newCode : newCode + "\n";
      return open + normalised + close;
    }
    matchCount++;
    return match;
  });

  return result;
}

/**
 * getCodeBlockIndex
 *
 * Returns the 0-based index of a specific code block occurrence within a
 * markdown content string, matched by language tag and exact code content.
 *
 * Used by CodeBlock to identify which block instance it represents, so the
 * correct block is targeted when multiple blocks share the same language tag.
 *
 * @param {string} content  - Full markdown message content
 * @param {string} langTag  - Language tag (e.g. "html-viz")
 * @param {string} code     - The exact original code string in the block (no trailing newline)
 * @returns {number}        - 0-based index, or 0 if not found (safe fallback)
 */
export function getCodeBlockIndex(content, langTag, code) {
  const pattern = new RegExp(
    "`{3}" + escapeRegex(langTag) + "\\n([\\s\\S]*?)`{3}",
    "g"
  );

  let match;
  let index = 0;
  // Normalise trailing newline for comparison (ReactMarkdown strips it via REGEX_MARKDOWN_CODE_REMOVES_NEWLINE)
  const normalised = code.endsWith("\n") ? code : code + "\n";

  while ((match = pattern.exec(content)) !== null) {
    if (match[1] === normalised) return index;
    index++;
  }

  return 0;
}

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
